// ABOUTME: Custom hook for file validation logic
// ABOUTME: Handles file size, type, and custom validation with memoization

import { useMemo, useCallback } from 'react';
import { FileValidationResult, FileValidatorFn } from '../types/types';
import { FILE_UPLOAD_CONSTANTS, MIME_TYPE_MAP } from '../utils/constants';

interface UseFileValidationOptions {
  maxFileSize?: number;
  minFileSize?: number;
  acceptedFileTypes?: string[];
  customValidators?: FileValidatorFn[];
  validateMimeType?: boolean;
}

export const useFileValidation = (options: UseFileValidationOptions = {}) => {
  const {
    maxFileSize = FILE_UPLOAD_CONSTANTS.MAX_FILE_SIZE,
    minFileSize = FILE_UPLOAD_CONSTANTS.MIN_FILE_SIZE,
    acceptedFileTypes = FILE_UPLOAD_CONSTANTS.DEFAULT_ACCEPTED_TYPES,
    customValidators = [],
    validateMimeType = true,
  } = options;

  // Memoize accepted extensions and MIME types
  const acceptedExtensions = useMemo(
    () => new Set(acceptedFileTypes.map(ext => ext.toLowerCase())),
    [acceptedFileTypes],
  );

  const acceptedMimeTypes = useMemo(() => {
    const mimeTypes = new Set<string>();
    acceptedFileTypes.forEach(ext => {
      const types = MIME_TYPE_MAP[ext.toLowerCase()];
      if (types) {
        types.forEach(type => mimeTypes.add(type));
      }
    });
    return mimeTypes;
  }, [acceptedFileTypes]);

  // Main validation function
  const validateFile = useCallback(
    (file: File): FileValidationResult => {
      // Check file size - minimum
      if (file.size < minFileSize) {
        return {
          valid: false,
          error: FILE_UPLOAD_CONSTANTS.ERRORS.FILE_TOO_SMALL,
        };
      }

      // Check file size - maximum
      if (file.size > maxFileSize) {
        return {
          valid: false,
          error: FILE_UPLOAD_CONSTANTS.ERRORS.FILE_TOO_LARGE(maxFileSize),
        };
      }

      // Extract file extension
      const fileName = file.name.toLowerCase();
      const lastDotIndex = fileName.lastIndexOf('.');

      if (lastDotIndex === -1) {
        return {
          valid: false,
          error: 'File must have an extension',
        };
      }

      const extension = fileName.substring(lastDotIndex).toLowerCase();

      // Check file extension
      if (!acceptedExtensions.has(extension)) {
        return {
          valid: false,
          error: FILE_UPLOAD_CONSTANTS.ERRORS.INVALID_TYPE(acceptedFileTypes),
        };
      }

      // Validate MIME type if enabled
      if (validateMimeType && file.type) {
        const isValidMimeType = acceptedMimeTypes.has(file.type);
        if (!isValidMimeType) {
          // Check if it's a known extension with mismatched MIME type
          const expectedTypes = MIME_TYPE_MAP[extension];
          if (expectedTypes && !expectedTypes.includes(file.type)) {
            return {
              valid: true,
              warning: `File type mismatch: expected ${expectedTypes.join(' or ')} but got ${file.type}`,
            };
          }
        }
      }

      // Run custom validators
      for (const validator of customValidators) {
        const result = validator(file);
        if (!result.valid) {
          return result;
        }
      }

      return { valid: true };
    },
    [
      maxFileSize,
      minFileSize,
      acceptedExtensions,
      acceptedMimeTypes,
      acceptedFileTypes,
      validateMimeType,
      customValidators,
    ],
  );

  // Batch validation for multiple files
  const validateFiles = useCallback(
    (
      files: File[],
    ): {
      validFiles: File[];
      invalidFiles: Array<{ file: File; error: string }>;
    } => {
      const validFiles: File[] = [];
      const invalidFiles: Array<{ file: File; error: string }> = [];

      files.forEach(file => {
        const result = validateFile(file);
        if (result.valid) {
          validFiles.push(file);
        } else {
          invalidFiles.push({ file, error: result.error || 'Invalid file' });
        }
      });

      return { validFiles, invalidFiles };
    },
    [validateFile],
  );

  // Check if a file type is accepted
  const isFileTypeAccepted = useCallback(
    (fileName: string): boolean => {
      const extension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
      return acceptedExtensions.has(extension);
    },
    [acceptedExtensions],
  );

  return {
    validateFile,
    validateFiles,
    isFileTypeAccepted,
    acceptedTypes: acceptedFileTypes,
    maxFileSize,
    minFileSize,
  };
};
